import type {
  ActivityEntityConfig,
  ChangesMap,
  RawActivity,
} from "./activityConfigs/types";
import type { ActivityEvent } from "@/components/ui/ActivityFeed/types";

// ─── Avatar helpers ───────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#E8734A",
  "#3b82f6",
  "#a855f7",
  "#22c55e",
  "#eab308",
  "#ec4899",
];

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ─── Change value formatting ──────────────────────────────────────────────────

/** Format a raw metadata value for display in field-level diff rows. */
export function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  const str = String(value);
  // ISO date strings
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
    const date = new Date(str);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  }
  // Numeric timestamps (13-digit ms-since-epoch)
  if (/^\d{13}$/.test(str)) {
    const date = new Date(Number(str));
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  }
  if (str === "true") return "Yes";
  if (str === "false") return "No";
  if (str.length > 60) return `${str.slice(0, 60)}…`;
  return str;
}

// ─── Changes extraction ───────────────────────────────────────────────────────

/**
 * Extract and format field-level changes from raw event metadata.
 * Returns undefined if no valid changes object is present.
 */
export function extractChanges(
  metadata: Record<string, unknown> | null | undefined,
): ChangesMap | undefined {
  if (!metadata) return undefined;
  const raw = metadata.changes;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;

  const result: ChangesMap = {};
  for (const [field, diff] of Object.entries(raw as Record<string, unknown>)) {
    if (diff && typeof diff === "object" && !Array.isArray(diff)) {
      const d = diff as Record<string, unknown>;
      result[field] = {
        from: formatChangeValue(d.from),
        to: formatChangeValue(d.to),
      };
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

// ─── Metadata sanitization ────────────────────────────────────────────────────

export const DEFAULT_METADATA_SKIP_KEYS = new Set([
  "changes",
  "success",
  "data",
  "error",
  "requestId",
]);

/**
 * Sanitize raw activity metadata for UI display.
 * Strips objects, arrays, raw ID fields, and keys in skipKeys.
 */
export function sanitizeActivityMetadata(
  metadata: Record<string, unknown> | null | undefined,
  skipKeys: Set<string> = DEFAULT_METADATA_SKIP_KEYS,
): Record<string, string> | undefined {
  if (!metadata) return undefined;

  const entries: [string, string][] = [];

  for (const [key, value] of Object.entries(metadata)) {
    if (skipKeys.has(key)) continue;
    if (key.toLowerCase().endsWith("id")) continue;
    if (value !== null && typeof value === "object") continue;
    if (value === null || value === undefined) continue;

    const stringVal = String(value);
    if (stringVal === "[object Object]" || stringVal.trim() === "") continue;

    const displayKey = key
      .replace(/([A-Z])/g, " $1")
      .trim()
      .toLowerCase();
    entries.push([displayKey, stringVal]);
  }

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

// ─── Transformer factory ──────────────────────────────────────────────────────

function resolveTitle<T extends string>(
  config: ActivityEntityConfig<T>,
  raw: RawActivity<T>,
  changes: ChangesMap | undefined,
): string {
  if (changes && raw.type === "update") {
    const fields = Object.keys(changes);
    if (fields.length === 1) {
      return (
        config.fieldTitleLabels[fields[0]] ??
        (config.fieldLabels[fields[0]]
          ? `Updated ${config.fieldLabels[fields[0]].toLowerCase()}`
          : `Updated ${fields[0]}`)
      );
    }
    if (fields.length > 1) {
      return `Updated ${fields.length} fields`;
    }
  }
  const label = config.eventTypeLabels[raw.type as T];
  if (typeof label === "function") {
    return label(raw);
  }
  return label ?? raw.description;
}

/**
 * Build a typed transformer function from an ActivityEntityConfig.
 * The returned function maps raw backend activity objects → ActivityEvent.
 */
export function createActivityTransformer<T extends string>(
  config: ActivityEntityConfig<T>,
): (raw: RawActivity<T>) => ActivityEvent {
  return (raw: RawActivity<T>): ActivityEvent => {
    const isSystemEvent = !raw.userName;
    const actorName = isSystemEvent
      ? config.systemActorName
      : (raw.userName ?? "Unknown");

    const changes = extractChanges(raw.metadata);
    const title = config.getTitle
      ? config.getTitle(raw, changes)
      : resolveTitle(config, raw, changes);

    return {
      id: raw.id,
      type: raw.type,
      category:
        config.eventTypeCategories[raw.type as T] ?? config.defaultCategory,
      title,
      actor: {
        name: actorName,
        initials: getInitials(actorName),
        avatarColor: getAvatarColor(actorName),
      },
      timestamp: new Date(raw.timestamp),
      changes,
      metadata: sanitizeActivityMetadata(raw.metadata, config.metadataSkipKeys),
    };
  };
}
